<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';
import KanbanMenu from './KanbanMenu.vue';
import AnimatedCounter from './AnimatedCounter.vue';

// Coluna apresentacional: recebe os cards já filtrados/ordenados do board
// (é isso que faz os contadores refletirem os filtros).
const props = defineProps({
  stage: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'move',
  'card-action',
  'card-click',
  'stage-action',
  'drag-state',
]);

const { t } = useI18n();

const dragDepth = ref(0);
const isOver = computed(() => dragDepth.value > 0);

// vuedraggable precisa de um array gravável; a fonte da verdade segue no store,
// então o setter é no-op e o reposicionamento real vem pelo @change.
const list = computed({
  get: () => props.cards,
  set: () => {},
});

const onChange = ({ added }) => {
  if (!added) return;
  dragDepth.value = 0;
  emit('move', { conversation: added.element, stageId: props.stage.id });
};

const stageMenuItems = computed(() => [
  {
    label: t('KANBAN.STAGE.MENU.RENAME'),
    value: 'rename',
    action: 'rename',
    icon: 'i-lucide-pencil',
  },
  {
    label: t('KANBAN.STAGE.MENU.COLOR'),
    value: 'color',
    action: 'color',
    icon: 'i-lucide-palette',
  },
  {
    label: t('KANBAN.STAGE.MENU.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash-2',
  },
]);
</script>

<template>
  <section
    class="kanban-column flex flex-col w-80 shrink-0 h-full rounded-xl bg-n-solid-1 outline outline-1 outline-n-container transition-[outline-color,box-shadow,background-color] duration-150"
    :class="{ 'kanban-column--over': isOver }"
    :style="{ '--stage-color': stage.color || '#9CA3AF' }"
    @dragenter.prevent="dragDepth += 1"
    @dragleave="dragDepth = Math.max(0, dragDepth - 1)"
    @drop="dragDepth = 0"
  >
    <header
      class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak select-none"
    >
      <!-- só esta área arrasta a coluna; o ⋯ fica de fora de propósito -->
      <div
        class="kanban-column-handle flex items-center flex-1 gap-2 min-w-0 cursor-grab active:cursor-grabbing"
      >
        <span
          class="i-lucide-grip-vertical size-3.5 text-n-slate-9 opacity-0 group-hover/board:opacity-100 transition-opacity shrink-0"
        />
        <span
          class="size-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: stage.color || '#9CA3AF' }"
        />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ stage.name }}
        </span>
      </div>
      <AnimatedCounter :value="cards.length" />
      <KanbanMenu
        :menu-items="stageMenuItems"
        :trigger-label="t('KANBAN.STAGE.MENU.LABEL')"
        @action="payload => emit('stage-action', { ...payload, stage })"
      />
    </header>

    <draggable
      v-model="list"
      :group="{ name: 'kanban-board', pull: true, put: true }"
      item-key="id"
      :animation="180"
      ghost-class="kanban-ghost"
      chosen-class="kanban-chosen"
      drag-class="kanban-drag"
      class="relative flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[4rem]"
      @change="onChange"
      @start="emit('drag-state', true)"
      @end="emit('drag-state', false)"
    >
      <template #item="{ element }">
        <div
          :key="element.id"
          class="kanban-card-in"
          @click="emit('card-click', element)"
        >
          <KanbanCard
            :conversation="element"
            @action="payload => emit('card-action', payload)"
          />
        </div>
      </template>
    </draggable>

    <p v-if="!cards.length" class="px-3 pb-3 text-xs text-n-slate-10">
      {{ t('KANBAN.COLUMN.EMPTY') }}
    </p>
  </section>
</template>

<style scoped>
.kanban-ghost {
  @apply opacity-40 outline-dashed outline-2 outline-n-brand;
}
.kanban-chosen {
  @apply shadow-lg;
}
.kanban-drag {
  @apply rotate-2 shadow-xl;
}

.kanban-column--over {
  box-shadow:
    0 0 0 2px var(--stage-color),
    0 0 0 6px color-mix(in srgb, var(--stage-color) 20%, transparent);
  @apply bg-n-alpha-1;
}

/* Entrada dos cards em CSS puro.
   NÃO usar <TransitionGroup> junto do vuedraggable: os nós que ele mantém no
   DOM durante a saída quebram o mapeamento interno da lib
   ("Cannot set properties of null (setting '__draggable_context')") e o
   arrasto passa a lançar exceção em onDragStart/onDragRemove.
   O movimento entre/dentro das colunas já é animado pelo :animation do Sortable. */
.kanban-card-in {
  animation: kanban-card-in 180ms ease-out both;
}

@keyframes kanban-card-in {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kanban-card-in {
    animation: none;
  }
  .kanban-drag {
    @apply rotate-0;
  }
}
</style>
